<template>
  <div class="eu-player">
    <div :id="containerId" class="eu-player-canvas"></div>
    <div class="eu-player-loading" v-if="loading">
      <i class="el-icon-loading"></i>
    </div>
    <div class="eu-player-error" v-if="errorMsg">{{errorMsg}}</div>
  </div>
</template>

<script>
// eu-core H5 播放器（替换原 MoviePlayer.swf）
// source 支持动画ID（走 /api/storage 接口，需要 token）或剧本文件 URL（分享页）
export default {
  props: {
    // 动画ID 或 剧本URL
    source: { type: String, default: '' },
    // 容器DOM id，同页面多个实例时需唯一
    containerId: { type: String, default: 'eu-player-canvas' },
    // 加载完成后自动播放
    autoplay: { type: Boolean, default: true }
  },
  data () {
    return {
      engine: null,
      instanceId: null,
      loading: false,
      // 加载完成前点了播放：挂起等loaded后补播（否则点击落在空movie上是空操作，封面已消失只能再点一次）
      pendingPlay: false,
      errorMsg: ''
    }
  },
  methods: {
    initPlayer () {
      this.engine = window.EuCoreEngine && window.EuCoreEngine.getInstance()
      if (!this.engine) {
        this.errorMsg = '播放器引擎未加载'
        return false
      }
      // API走同源相对路径，与 Scene.vue 保持一致
      if (window.EuCoreEngine.setAPIUrl) {
        window.EuCoreEngine.setAPIUrl('')
      }
      this.syncToken()
      const el = document.getElementById(this.containerId)
      // width/height 传 <=1 的比率：Player.onResize 按 stageWidth 比例跟随画布，
      // 全屏/窗口变化时影片自动重缩放；传绝对像素会锁死尺寸导致全屏不放大
      this.instanceId = this.engine.createPlayer(this.containerId, {
        width: 1,
        height: 1,
        aspectRatio: '16:9',
        backgroundColor: 0x000000,
        autoScale: true,
        interactionEnable: true
      })
      this.engine.on('loadProgress', (data) => {
        if (data.instanceId === this.instanceId) {
          this.$emit('progress', data.progress)
        }
      })
      this.engine.on('loadError', (data) => {
        if (data.instanceId === this.instanceId) {
          this.loading = false
          this.errorMsg = '动画加载失败'
        }
      })
      this.engine.on('playComplete', (data) => {
        if (data.instanceId === this.instanceId) {
          this.$emit('playComplete')
        }
      })
      this.engine.on('playStop', (data) => {
        if (data.instanceId === this.instanceId) {
          this.$emit('playStop')
        }
      })
      return true
    },
    syncToken () {
      // 与 Scene.vue 一致：token 存在 localStorage.login_status.tokenId
      if (!window.EuCoreEngine || !window.EuCoreEngine.setAPIToken) return
      try {
        const loginStatus = JSON.parse(localStorage.getItem('login_status') || 'null')
        const token = loginStatus && loginStatus.tokenId
        if (token) {
          window.EuCoreEngine.setAPIToken(token)
        }
      } catch (e) { /* ignore */ }
    },
    loadAndPlay (source) {
      if (!this.instanceId || !source) return
      this.loading = true
      this.errorMsg = ''
      this.engine.loadAnimation(this.instanceId, source).then(() => {
        this.loading = false
        if (this.autoplay || this.pendingPlay) {
          this.engine.play(this.instanceId)
        }
        this.pendingPlay = false
        // 带出影片实际宽高：宿主按此调整容器宽高比（竖屏9:16影片等）
        const size = this.engine.getMovieSize && this.engine.getMovieSize(this.instanceId)
        this.$emit('loaded', size)
      }).catch((e) => {
        this.loading = false
        this.errorMsg = '动画加载失败'
        this.$emit('error', e)
      })
    },
    play () {
      if (!this.instanceId) return
      if (this.loading) {
        this.pendingPlay = true
        return
      }
      this.engine.play(this.instanceId)
    },
    pause () { if (this.instanceId) this.engine.pause(this.instanceId) },
    // 暂停后续播（保留步内进度）
    resume () {
      if (this.instanceId && this.engine.resume) this.engine.resume(this.instanceId)
    },
    stop () { if (this.instanceId) this.engine.stop(this.instanceId) },
    // seek到全片指定毫秒（进度条拖动）
    seek (ms) {
      if (this.instanceId && this.engine.seekToTime) {
        this.engine.seekToTime(this.instanceId, ms)
      }
    },
    // 获取播放状态 {playing, current(ms), total(ms)}，未加载返回null
    getPlayState () {
      if (!this.instanceId || !this.engine.getPlayState) return null
      return this.engine.getPlayState(this.instanceId)
    },
    // 从头重新播放
    replay () {
      if (!this.instanceId) return
      this.seek(0)
      this.play()
    }
  },
  mounted () {
    this.$nextTick(() => {
      if (this.initPlayer()) {
        this.loadAndPlay(this.source)
      }
    })
  },
  beforeDestroy () {
    if (this.engine && this.instanceId) {
      this.engine.destroyInstance(this.instanceId)
      this.instanceId = null
    }
  }
}
</script>

<style lang="stylus">
  .eu-player
    position: relative
    width: 100%
    height: 100%
    .eu-player-canvas
      width: 100%
      height: 100%
    .eu-player-loading
      position: absolute
      left: 0
      top: 0
      width: 100%
      height: 100%
      display: flex
      align-items: center
      justify-content: center
      color: #ffffff
      font-size: 32px
    .eu-player-error
      position: absolute
      left: 0
      top: 0
      width: 100%
      height: 100%
      display: flex
      align-items: center
      justify-content: center
      color: #dddddd
      font-size: 16px
</style>



// WEBPACK FOOTER //
// EuPlayer.vue?58b2f323